import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { Pencil, Sparkles, UserPlus2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'

import * as authApi from '@/features/auth/auth-api'
import { useAuth } from '@/features/auth/auth-context'
import { getInvitationByToken } from '@/features/settings/invitations-api'
import { Button } from '@/shared/components/ui/button'
import { Card, CardBody, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Field } from '@/shared/components/ui/field'
import { Input } from '@/shared/components/ui/input'

import { AuthShell } from '../components/auth-shell'
import { PhoneInput } from '../components/phone-input'

const phoneSchema = z.object({
  phone: z.string().regex(/^\+7\d{10}$/, 'Введите номер полностью'),
})

const confirmSchema = z
  .object({
    code: z.string().regex(/^\d{4}$/, 'Введите код из 4 цифр'),
    name: z.string().min(2, 'Введите имя'),
    password: z.string().min(8, 'Минимум 8 символов'),
    password_confirmation: z.string().min(8, 'Минимум 8 символов'),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'Пароли не совпадают',
    path: ['password_confirmation'],
  })

type PhoneForm = z.infer<typeof phoneSchema>
type ConfirmForm = z.infer<typeof confirmSchema>

function extractErrorMessage(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message
  return message || fallback
}

export function RegisterPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const referralSlug = searchParams.get('ref')?.trim() || undefined
  const { register, isAuthenticated } = useAuth()
  const [step, setStep] = useState<'phone' | 'confirm'>('phone')
  const [resendIn, setResendIn] = useState(0)
  const [isResending, setIsResending] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [verificationToken, setVerificationToken] = useState<string | null>(null)

  const fromPathname = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname
  const inviteToken = fromPathname?.match(/^\/invite\/(.+)$/)?.[1]

  const invitationQuery = useQuery({
    queryKey: ['invitation', inviteToken],
    queryFn: () => getInvitationByToken(inviteToken as string),
    enabled: Boolean(inviteToken),
    retry: false,
  })
  const invitation = invitationQuery.data

  const phoneForm = useForm<PhoneForm>({
    resolver: zodResolver(phoneSchema),
    defaultValues: { phone: '' },
  })

  const confirmForm = useForm<ConfirmForm>({
    resolver: zodResolver(confirmSchema),
    defaultValues: { code: '', name: '', password: '', password_confirmation: '' },
  })

  useEffect(() => {
    if (resendIn <= 0) return
    const timer = setTimeout(() => setResendIn((value) => value - 1), 1000)
    return () => clearTimeout(timer)
  }, [resendIn])

  if (isAuthenticated) {
    return <Navigate replace to="/projects" />
  }

  const phone = phoneForm.watch('phone')

  const sendCode = async () => {
    setVerificationToken(null)
    const payload = await authApi.sendVerificationCode(phone)
    setResendIn(payload.resend_after)
  }

  const onSubmitPhone = phoneForm.handleSubmit(async () => {
    setErrorMessage(null)

    try {
      await sendCode()
      setStep('confirm')
    } catch (error) {
      setErrorMessage(extractErrorMessage(error, 'Не удалось отправить SMS. Попробуйте позже.'))
    }
  })

  const onResend = async () => {
    setErrorMessage(null)
    setIsResending(true)

    try {
      await sendCode()
    } catch (error) {
      setErrorMessage(extractErrorMessage(error, 'Не удалось отправить SMS. Попробуйте позже.'))
    } finally {
      setIsResending(false)
    }
  }

  const onSubmitConfirm = confirmForm.handleSubmit(async (values) => {
    setErrorMessage(null)

    try {
      const token = verificationToken ?? (await authApi.verifyPhoneCode(phone, values.code))
      setVerificationToken(token)
      await register({
        name: values.name,
        phone,
        verification_token: token,
        password: values.password,
        password_confirmation: values.password_confirmation,
        ref: referralSlug,
        invitation_token: inviteToken,
      })
      const redirect = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname
      navigate(redirect ?? '/projects', { replace: true })
    } catch (error) {
      setErrorMessage(
        extractErrorMessage(error, 'Не удалось зарегистрироваться. Проверьте введённые данные.'),
      )
    }
  })

  const onEditPhone = () => {
    setErrorMessage(null)
    setVerificationToken(null)
    confirmForm.reset()
    setStep('phone')
  }

  return (
    <AuthShell>
      <Card className="w-full">
        <CardHeader>
          <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--color-secondary)]/20 text-[var(--color-secondary)]">
            <Sparkles className="h-5 w-5" />
          </div>
          <CardTitle>Создание аккаунта</CardTitle>
          <CardDescription>
            {step === 'phone'
              ? 'Укажите номер телефона — мы отправим SMS с кодом подтверждения.'
              : `Код отправлен на номер ${phone}.`}
            {invitation ? (
              <>
                <br />
                Регистрация по приглашению для {invitation.email}
              </>
            ) : null}
          </CardDescription>
        </CardHeader>

        <CardBody>
          {step === 'phone' ? (
            <form className="space-y-4" onSubmit={onSubmitPhone}>
              <Field error={phoneForm.formState.errors.phone?.message} label="Телефон">
                {(props) => (
                  <Controller
                    control={phoneForm.control}
                    name="phone"
                    render={({ field }) => (
                      <PhoneInput
                        invalid={Boolean(phoneForm.formState.errors.phone)}
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                        value={field.value}
                        {...props}
                      />
                    )}
                  />
                )}
              </Field>

              {errorMessage ? (
                <p className="text-sm text-[var(--color-danger-fg)]">{errorMessage}</p>
              ) : null}

              <Button className="w-full" loading={phoneForm.formState.isSubmitting} type="submit">
                <UserPlus2 className="h-4 w-4" />
                Получить код
              </Button>
            </form>
          ) : (
            <form className="space-y-4" onSubmit={onSubmitConfirm}>
              <button
                className="inline-flex items-center gap-1 text-sm text-[var(--color-accent)] hover:underline"
                onClick={onEditPhone}
                type="button"
              >
                <Pencil className="h-3.5 w-3.5" />
                Изменить номер
              </button>

              <Field error={confirmForm.formState.errors.code?.message} label="Код из SMS">
                {(props) => (
                  <Input
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    maxLength={4}
                    placeholder="1234"
                    type="text"
                    {...confirmForm.register('code')}
                    {...props}
                  />
                )}
              </Field>

              <Field error={confirmForm.formState.errors.name?.message} label="Имя">
                {(props) => (
                  <Input
                    autoComplete="name"
                    placeholder="Vlad"
                    type="text"
                    {...confirmForm.register('name')}
                    {...props}
                  />
                )}
              </Field>

              <Field error={confirmForm.formState.errors.password?.message} label="Пароль">
                {(props) => (
                  <Input
                    autoComplete="new-password"
                    placeholder="••••••••"
                    type="password"
                    {...confirmForm.register('password')}
                    {...props}
                  />
                )}
              </Field>

              <Field
                error={confirmForm.formState.errors.password_confirmation?.message}
                label="Повтор пароля"
              >
                {(props) => (
                  <Input
                    autoComplete="new-password"
                    placeholder="••••••••"
                    type="password"
                    {...confirmForm.register('password_confirmation')}
                    {...props}
                  />
                )}
              </Field>

              {errorMessage ? (
                <p className="text-sm text-[var(--color-danger-fg)]">{errorMessage}</p>
              ) : null}

              <Button className="w-full" loading={confirmForm.formState.isSubmitting} type="submit">
                <UserPlus2 className="h-4 w-4" />
                Создать аккаунт
              </Button>

              <p className="text-sm text-[var(--color-text-soft)]">
                {resendIn > 0 ? (
                  <>Отправить код ещё раз можно через {resendIn} сек.</>
                ) : (
                  <button
                    className="font-medium text-[var(--color-accent)] hover:underline disabled:opacity-50"
                    disabled={isResending}
                    onClick={onResend}
                    type="button"
                  >
                    Отправить код ещё раз
                  </button>
                )}
              </p>
            </form>
          )}

          <p className="mt-4 text-sm text-[var(--color-text-soft)]">
            Уже есть аккаунт?{' '}
            <Link className="font-medium text-[var(--color-accent)] hover:underline" to="/login">
              Войти
            </Link>
          </p>
        </CardBody>
      </Card>
    </AuthShell>
  )
}
